import { escapeRegExp } from "./util";

export function getQueryValue(key: string, url = location.href): string | null {
  // Old iOS apps are using a wrong format url
  if (url.includes("?state=bank/access_token=")) {
    url = url.replace("?state=bank/access_token=", "?state=bank&access_token=");
  }

  const match = url.match(new RegExp(`[#?&/]${escapeRegExp(key)}=([^&#]*)`));

  if (match === null) {
    return null;
  }

  // Remove the first character of the value
  // if it is a slash. See test cases.
  if (match[1]?.[0] === "/") {
    return match[1].substring(1);
  }

  // Remove all trailing slashes of the value.
  // see test cases.
  return match[1]?.replace(/\/+$/, "") || "";
}

/**
 * Navigate to a new url.
 * Will only accept 'https:', 'http:', 'bankid:', 'swish:', 'mailto:', 'tel:' urls.
 *
 * - Using the "replace" type will replace the current url with new new one, meaning the
 * user won't be able to use the back button to navigate to it.
 * - "assign" (default value) will just push the new url to the history.
 * - If executing in a iframe, we will use `window.open`.
 * - Using "open-blank" will result in opening the url in a new tab. Will return a window
 * object, representing the newly created window. If the window couldn't be opened, the
 * returned value is instead `null`.
 */
export function navigate(url: string, type: "open-blank"): Window | null;
export function navigate(url: string, type?: "assign" | "replace"): void;
export function navigate(
  url: string,
  type: "assign" | "replace" | "open-blank" = "assign",
): void | Window | null {
  assertUrl(url);
  if (window.top !== window) {
    window.open(url, "_parent");
  } else if (type === "replace") {
    window.location.replace(url);
  } else if (type === "open-blank") {
    return window.open(url, "_blank", "noreferrer,noopener");
  } else {
    window.location.assign(url);
  }
}

function assertUrl(url: string): asserts url is string {
  try {
    const parsedUrl = new URL(url);
    if (
      ![
        "https:",
        "http:",
        "bankid:",
        "swish:",
        "mailto:",
        "tel:",
        "kivra:",
        "kivrafi:",
      ].includes(parsedUrl.protocol)
    ) {
      throw new Error();
    }
  } catch {
    throw new TypeError(`url: "${url}" is not a valid url`);
  }
}

export function getAndDecodeOption<Value extends string>(
  name: string,
  url = location.href,
): Value | undefined {
  const value = getQueryValue(name, url);
  // decodeUriComponent does not handle '+' which is valid encoding for space in the query string
  if (!value) {
    return undefined;
  }
  return decodeURIComponent(value.replace(/\+/g, " ")) as Value;
}

export const serializeQueryParams = (
  paramObject: Record<string, string | number | boolean | null | undefined>,
): string => {
  const entries = Object.entries(paramObject);
  return entries.length > 0
    ? `?${
      entries
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join("&")
    }`
    : "";
};

export const getPathSegmentAfter = (
  url: string,
  priorSegment: string,
): string | undefined => {
  const segments = url.split("/");
  const priorIndex = segments.indexOf(priorSegment);
  if (priorIndex === -1) {
    return;
  }
  return segments[priorIndex + 1];
};
